"use client";
import { useAppDispatch, useAppSelector } from "../../lib/redux/hooks";
import { 
  changeWorkExperiences, 
  addSectionInForm, 
  deleteSectionInFormByIdx,
  moveSectionInForm,
  selectWorkExperiences 
} from "../../lib/redux/resumeSlice";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";

export const WorkExperiencesForm = () => {
  const dispatch = useAppDispatch();
  const workExperiences = useAppSelector(selectWorkExperiences);

  const handleWorkExperienceChange = (
    idx: number,
    field: keyof typeof workExperiences[0],
    value: string | string[]
  ) => {
    dispatch(changeWorkExperiences({ idx, field, value }));
  };

  const handleAddWorkExperience = () => {
    dispatch(addSectionInForm({ form: "workExperiences" }));
  };

  const handleDeleteWorkExperience = (idx: number) => {
    dispatch(deleteSectionInFormByIdx({ form: "workExperiences", idx }));
  };

  const handleMoveWorkExperience = (idx: number, direction: "up" | "down") => {
    dispatch(moveSectionInForm({ form: "workExperiences", idx, direction }));
  };

  return (
    <Card className="bg-white border-2 border-gray-800 shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between bg-gray-900 text-white">
        <CardTitle className="text-lg font-semibold">Work Experience</CardTitle>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddWorkExperience}
          className="border-white text-white hover:bg-white hover:text-black transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Experience
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {workExperiences.map((experience, idx) => (
          <div key={idx} className="space-y-4 p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-black">Experience {idx + 1}</h4>
              <div className="flex items-center space-x-2">
                {workExperiences.length > 1 && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleMoveWorkExperience(idx, "up")}
                      disabled={idx === 0}
                      className="border-gray-400 text-gray-600 hover:border-black hover:text-black transition-colors"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleMoveWorkExperience(idx, "down")}
                      disabled={idx === workExperiences.length - 1}
                      className="border-gray-400 text-gray-600 hover:border-black hover:text-black transition-colors"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteWorkExperience(idx)}
                  disabled={workExperiences.length === 1}
                  className="border-red-300 text-red-600 hover:border-red-600 hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

                         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <div className="space-y-2">
                 <Label htmlFor={`company-${idx}`} className="text-black font-semibold">Company</Label>
                 <Input
                   id={`company-${idx}`}
                   value={experience.company}
                   onChange={(e) => handleWorkExperienceChange(idx, "company", e.target.value)}
                   placeholder="Company Name"
                   className="border-2 border-gray-300 bg-white text-black placeholder-gray-500 hover:border-black focus:border-black transition-colors"
                 />
               </div>
               
               <div className="space-y-2">
                 <Label htmlFor={`jobTitle-${idx}`} className="text-black font-semibold">Job Title</Label>
                 <Input
                   id={`jobTitle-${idx}`}
                   value={experience.jobTitle}
                   onChange={(e) => handleWorkExperienceChange(idx, "jobTitle", e.target.value)}
                   placeholder="Software Engineer"
                   className="border-2 border-gray-300 bg-white text-black placeholder-gray-500 hover:border-black focus:border-black transition-colors"
                 />
               </div>
               
               <div className="space-y-2 md:col-span-2">
                 <Label htmlFor={`date-${idx}`} className="text-black font-semibold">Duration</Label>
                 <Input
                   id={`date-${idx}`}
                   value={experience.date}
                   onChange={(e) => handleWorkExperienceChange(idx, "date", e.target.value)}
                   placeholder="Jan 2020 - Present"
                   className="border-2 border-gray-300 bg-white text-black placeholder-gray-500 hover:border-black focus:border-black transition-colors"
                 />
               </div>
             </div>

             <div className="space-y-2">
               <Label htmlFor={`descriptions-${idx}`} className="text-black font-semibold">Responsibilities & Achievements</Label>
               <Textarea
                 id={`descriptions-${idx}`}
                 value={experience.descriptions.join('\n')}
                 onChange={(e) => {
                   const descriptions = e.target.value.split('\n').filter(line => line.trim());
                   handleWorkExperienceChange(idx, "descriptions", descriptions);
                 }}
                 placeholder="• Led development of key features...
 • Improved performance by 25%...
 • Mentored junior developers..."
                 rows={4}
                 className="border-2 border-gray-300 bg-white text-black placeholder-gray-500 hover:border-black focus:border-black transition-colors resize-none"
               />
               <p className="text-sm text-gray-600">
                 Enter each responsibility on a new line starting with a bullet point (•)
               </p>
             </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
